import React from "react";
import { Category, Channel, defaultFormData, Language, TemplateCreatorProps, TemplateFormData } from "../../../views/marketing-pages/templates/types";
import Input from "../../input";
import { PhoneMockup } from "../campaign/phone-mock";
import { SectionLabel } from "./utiz";
import { ChannelSelect } from "./channel-select";
import { WhatsAppForm } from "./whatsapp-form";
import { X } from "lucide-react";
import { channelOptions } from "../../../views/marketing-pages/templates/data";
import useApi from "../../hook/req";
import useRequest from "../../hook/use-req";
import { RcsForm } from "./rcs/rcs-form";
import { RcsPreview } from "./rcs/rcs-preview";
import { buttonToApiAction } from "./rcs/rcs-types";
import { toast } from "sonner";
import { SmsForm } from "./sms-form";
import { WhatsappMockUp } from "../campaign/whatsapp-mock";

import Emailform, { EmailFormData } from "./email-form"; // ← ADD EmailFormData
import { EmailMockUp } from "../campaign/email-mockup";
import { extractVariableIndices } from "./message-variables-section";
import { defaultsFromSavedVariables, htmlToPlainText, parseEmailDesign, restoreVariableNames } from "../../shared/email-design";

import { ButtonType, CtaButton, CtaButtonType, HeaderType, MediaType, QuickReplyButton } from "../../../views/marketing-pages/templates/types";

// Live Preview only: {{name}} shows its "Replace with" value when one is
// set, and stays as {{name}} when not. The saved content keeps the tags.
const fillEmailVariables = (text: string, vars?: { name: string; defaultValue: string }[]) =>
  text.replace(/\{\{\s*([\w.]+)\s*\}\}/g, (tag, name) => vars?.find((v) => v.name === name)?.defaultValue.trim() || tag);

// Maps a raw backend `buttons` array (as returned by GET /templates and
// GET /templates/{id} — [{type: "QUICK_REPLY"|"URL"|"PHONE_NUMBER", text, url, phone_number}])
// into this form's own button state, so opening an existing/approved
// template for view or copy actually shows what it really has instead of
// always starting from "None" regardless of the template's real buttons.
const hydrateButtonsFromTemplate = (
  rawButtons: unknown,
): { buttonType: ButtonType; quickReplyButtons: QuickReplyButton[]; ctaButtons: CtaButton[] } => {
  const list = Array.isArray(rawButtons) ? rawButtons : [];
  if (list.length === 0) {
    return { buttonType: 'none', quickReplyButtons: defaultFormData.quickReplyButtons, ctaButtons: defaultFormData.ctaButtons };
  }
  const isQuickReply = list.every((b: any) => b?.type === 'QUICK_REPLY');
  if (isQuickReply) {
    return {
      buttonType: 'quick_reply',
      quickReplyButtons: list.map((b: any, i: number) => ({ id: String(i + 1), label: b?.text || '' })),
      ctaButtons: defaultFormData.ctaButtons,
    };
  }
  return {
    buttonType: 'call_to_action',
    quickReplyButtons: defaultFormData.quickReplyButtons,
    ctaButtons: list.map((b: any, i: number) => {
      const type: CtaButtonType =
        b?.type === 'PHONE_NUMBER' || b?.type === 'VOICE_CALL' || b?.type === 'COPY_CODE' ? b.type : 'URL';
      const example = Array.isArray(b?.example) ? b.example[0] : b?.example;
      return {
        id: String(i + 1),
        type,
        text: b?.text || '',
        url: type === 'URL' ? (b?.url || '') : undefined,
        phoneNumber: type === 'PHONE_NUMBER' ? (b?.phone_number || '') : undefined,
        urlType: type === 'URL' && example ? 'dynamic' : 'static',
        example: (type === 'URL' || type === 'COPY_CODE') ? (example || '') : undefined,
      };
    }),
  };
};

// Maps a raw backend `header_type`/`header_content` (as returned by
// GET /templates and GET /templates/{id} — header_type is
// "none"|"text"|"image"|"video"|"document"|"file"|null) into this form's
// own Header section state, so opening an existing/approved template shows
// what header it really has (and the Live Preview can render it) instead of
// always starting from "None" regardless of the template's real header.
// `header_content` for a media header is usually an opaque WhatsApp media
// handle rather than a directly-browsable URL — it's still stored as-is in
// headerImage/headerVideo/headerDocument so the preview can at least show a
// type-correct placeholder for it (see WhatsappMockUp's own url check).
const hydrateHeaderFromTemplate = (
  rawHeaderType: unknown,
  rawHeaderContent: unknown,
): Pick<TemplateFormData, 'headerType' | 'headerText' | 'mediaType' | 'headerImage' | 'headerVideo' | 'headerDocument'> => {
  const content = typeof rawHeaderContent === 'string' ? rawHeaderContent : '';
  if (rawHeaderType === 'text') {
    return { headerType: 'text' as HeaderType, headerText: content, mediaType: defaultFormData.mediaType, headerImage: '', headerVideo: '', headerDocument: '' };
  }
  if (rawHeaderType === 'image' || rawHeaderType === 'video' || rawHeaderType === 'document' || rawHeaderType === 'file') {
    const mediaType: MediaType = rawHeaderType === 'file' ? 'document' : rawHeaderType;
    return {
      headerType: 'media' as HeaderType,
      headerText: '',
      mediaType,
      headerImage: mediaType === 'image' ? content : '',
      headerVideo: mediaType === 'video' ? content : '',
      headerDocument: mediaType === 'document' ? content : '',
    };
  }
  return { headerType: 'none' as HeaderType, headerText: '', mediaType: defaultFormData.mediaType, headerImage: '', headerVideo: '', headerDocument: '' };
};

// RCS wants a short language code ("en"), unlike WhatsApp/SMS/Email which
// send the raw Language enum string ("English (US)").
const RCS_LANGUAGE_CODE: Record<string, string> = {
  "English (US)": "en",
  "English (UK)": "en_GB",
  "Spanish": "es",
  "French": "fr",
};

// Map the frontend rcsComponents shape into the `components` array the
// unified /templates/ POST expects when channel === 'rcs' — same endpoint
// every other channel already uses, just with its own component shape
// instead of header/content/buttons. Media files should already be
// uploaded (their mediaUrl set) before this runs.
function mapRcsComponentsToApi(rcsComponents: TemplateFormData['rcsComponents']) {
  return (rcsComponents || []).map((comp) => {
    switch (comp.type) {
      case "text":
        return { type: "text", text: comp.content || "" };
      case "image":
      case "video":
        return {
          type: comp.type,
          media_url: comp.mediaUrl || "",
          text: comp.mediaCaption || "",
        };
      case "rich_card":
        return {
          type: "rich_card",
          title: comp.title || "",
          description: comp.description || "",
          media_url: comp.mediaUrl || "",
          actions: (comp.buttons || []).map(buttonToApiAction),
        };
      case "carousel":
        return {
          type: "carousel",
          text: comp.carouselText || "",
          cards: (comp.cards || []).map((card) => ({
            title: card.title || "",
            description: card.description || "",
            media_url: card.mediaUrl || "",
            actions: (card.buttons || []).map(buttonToApiAction),
          })),
        };
      case "suggested_replies":
        return {
          type: "suggested_replies",
          text: comp.content || "",
          replies: (comp.suggestedReplies || [])
            .filter((r) => r.text.trim())
            .map((r) => ({
              text: r.text,
              postback_data: r.postbackData || r.text.toLowerCase().replace(/\s+/g, "_"),
            })),
        };
      case "cta_buttons":
        return {
          type: "call_to_action",
          text: comp.ctaText || "",
          actions: (comp.buttons || []).map(buttonToApiAction),
        };
      case "location":
        return {
          type: "location",
          label: comp.locationLabel || "",
          latitude: comp.latitude,
          longitude: comp.longitude,
          text: comp.locationText || "",
        };
      case "divider":
        return { type: "divider" };
      default:
        return { type: comp.type };
    }
  });
}

export const TemplateCreator: React.FC<TemplateCreatorProps> = ({
  isOpen = true,
  templates,
  wabaAccounts,
  onClose,
  onSaveDraft,
  onSendRequest,
  selectedTemplate,
   initialChannel,
   asDrawer = false,
}) => {
  const [formData, setFormData] = React.useState<TemplateFormData>(defaultFormData);
  const [isCopyMode, setIsCopyMode] = React.useState(false);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const token = localStorage.getItem("authToken");

  const { makeRequest: createTemplate } = useApi("/templates/", "POST", {
    Authorization: `Bearer ${token}`,
  });

  const { makeRequest: uploadMedia } = useApi("/templates/media/upload", "POST", {
    Authorization: `Bearer ${token}`,
  });

  // PUT /templates/{id} — SMS-only update path (2026-08-14): editing an
  // existing SMS template previously always fell through to the same POST
  // /templates/ as creating a new one, silently creating a duplicate instead
  // of updating. Confirmed real body shape is narrower than create's — just
  // { content: { message, personalize, variable_column_map, variables } },
  // not the full template payload. useRequest (not useApi) since it's the
  // one that supports a pathParam for the id.
  const { makeRequest: updateSmsTemplate } = useRequest("/templates", "PUT", {
    Authorization: `Bearer ${token}`,
  });

  // RCS media (rich card / carousel images) still needs a real URL before
  // it can go in the JSON payload — uploaded via the same generic media
  // endpoint, then submitted through the same /templates/ POST as every
  // other channel (channel: "rcs").
  const uploadRcsMedia = async (file: File): Promise<string | null> => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("content_type", file.type);

    const [response] = await uploadMedia(fd);
    return response?.url || null;
  };

  const uploadAllRcsMedia = async (): Promise<typeof formData.rcsComponents> => {
    const components = formData.rcsComponents || [];
    const updatedComponents = [...components];

    for (let i = 0; i < updatedComponents.length; i++) {
      const comp = updatedComponents[i];

      if (comp.mediaFile) {
        const url = await uploadRcsMedia(comp.mediaFile);
        if (url) {
          updatedComponents[i] = { ...comp, mediaUrl: url, mediaFile: null };
        }
      }

      if (comp.type === 'carousel' && comp.cards) {
        const updatedCards = [...comp.cards];
        for (let j = 0; j < updatedCards.length; j++) {
          const card = updatedCards[j];
          if (card.mediaFile) {
            const url = await uploadRcsMedia(card.mediaFile);
            if (url) {
              updatedCards[j] = { ...card, mediaUrl: url, mediaFile: null };
            }
          }
        }
        updatedComponents[i] = { ...updatedComponents[i], cards: updatedCards };
      }
    }

    return updatedComponents;
  };

  const status = selectedTemplate?.wa_status?.toUpperCase();
  const isReadOnly =
    !isCopyMode &&
    !!selectedTemplate &&
    ['APPROVED', 'VERIFIED', 'IN_REVIEW', 'PENDING'].includes(status ?? '');

  React.useEffect(() => {
    if (selectedTemplate) {
      setIsCopyMode(false);
      const channelMap: Record<string, string> = {
        WhatsApp: 'whatsapp', whatsapp: 'whatsapp',
        SMS: 'sms', sms: 'sms',
        Email: 'email', email: 'email',
        RCS: 'rcs', rcs: 'rcs',
      };
      const categoryMap: Record<string, string> = {
        MARKETING: 'Marketing', AUTHENTICATION: 'Authentication', UTILITY: 'Utility',
      };
      const languageMap: Record<string, string> = {
        en_US: 'English (US)', en_GB: 'English (UK)',
        es_ES: 'Spanish', fr_FR: 'French',
      };

      const { buttonType, quickReplyButtons, ctaButtons } = hydrateButtonsFromTemplate(selectedTemplate.buttons);
      const headerFields = hydrateHeaderFromTemplate(selectedTemplate.header_type, selectedTemplate.header_content);

      setFormData({
        ...defaultFormData,
        channel: (channelMap[selectedTemplate.channel ?? 'whatsapp'] ?? 'whatsapp') as Channel,
        templateName: selectedTemplate.template_name ?? '',
        whatsappBusinessAccount: selectedTemplate.waba_id ?? '',
        category: (categoryMap[selectedTemplate.category ?? ''] ?? 'Marketing') as Category,
        language: (languageMap[selectedTemplate.language ?? ''] ?? 'English (US)') as Language,
        messageBody: selectedTemplate.content ?? '',
        messageVariables: selectedTemplate.variables ?? [],
        footer: selectedTemplate.footer ?? '',
        buttonType,
        quickReplyButtons,
        ctaButtons,
        ...headerFields,
        // Email: `content` holds the editor design (or plain text for a
        // Plain Text email), with variables saved as {{index}} — mapped
        // back to names here.
        ...((selectedTemplate.channel ?? '').toLowerCase() === 'email' && (() => {
          const design = parseEmailDesign(selectedTemplate.content, selectedTemplate.variables);
          const html = restoreVariableNames(selectedTemplate.html_content ?? '', selectedTemplate.variables);
          return {
            messageBody: '',
            emailSubject: selectedTemplate.subject ?? '',
            emailPreviewText: (selectedTemplate as any).preview_text ?? '',
            emailHtmlContent: html,
            // Editor-built HTML (a full document) belongs in the Email Editor,
            // not the Plain Text box.
            emailPlainText: design || /^\s*(<!doctype|<html)/i.test(html) ? '' : html,
            emailDesign: design,
            emailVariables: defaultsFromSavedVariables(selectedTemplate.variables),
          };
        })()),
      });
    } else {
      // If no selected template, use initialChannel if provided, otherwise use default
      setFormData({
        ...defaultFormData,
        channel: initialChannel || defaultFormData.channel,
      });
      setIsCopyMode(false);
    }
  }, [selectedTemplate, initialChannel]);

  const handleChange = (updates: Partial<TemplateFormData>) =>
    setFormData((prev) => ({ ...prev, ...updates }));

  const handleChannelChange = (channel: string) =>
    setFormData({ ...defaultFormData, channel: channel as Channel });

  const handleMakeCopy = () => {
    setIsCopyMode(true);
    setFormData((prev) => ({
      ...prev,
      templateName: `${prev.templateName}_Copy`,
    }));
  };

  // SMS (and any other channel without a wa_status/approval workflow) has no
  // status to show — status ends up `undefined`, and since `undefined +
  // string` coerces to the literal text "undefined" in JS, the badge was
  // rendering that word next to every SMS template's name. Only show it when
  // there's an actual status to report.
  const titleBadge = selectedTemplate && status ? (
    <span
      className={`ml-2 inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${
        ['APPROVED', 'VERIFIED'].includes(status ?? '')
          ? 'bg-green-100 dark:bg-green-500/15 text-green-700 dark:text-green-400'
          : ['IN_REVIEW', 'PENDING'].includes(status ?? '')
            ? 'bg-yellow-100 dark:bg-yellow-500/15 text-yellow-700 dark:text-yellow-400'
            : 'bg-red-100 dark:bg-red-500/15 text-red-700 dark:text-red-400'
      }`}
    >
      <span className="w-1.5 h-1.5 rounded-full bg-current" />
      {status === 'IN_REVIEW' ? 'Pending' :
        status?.charAt(0) + (status?.slice(1).toLowerCase() ?? '')}
    </span>
  ) : null;

const prepareTemplatePayload = (rcsComponentsOverride?: TemplateFormData['rcsComponents']) => {
  let headerType = "none";
  let headerContent = "";
  let headerFile: File | null = null;

  if (formData.headerType === 'text' && formData.headerText) {
    headerType = "text";
    headerContent = formData.headerText;
  } else if (formData.headerType === 'media' && formData.mediaFile) {
    switch (formData.mediaType) {
      case 'image': headerType = "image"; break;
      case 'video': headerType = "video"; break;
      case 'document': headerType = "document"; break;
      default: headerType = "image";
    }
    headerFile = formData.mediaFile;
  } else if (formData.headerType === 'media') {
    // Making a copy (or re-editing) an existing template without picking a
    // new file — the media itself hasn't changed. Without this branch the
    // whole media case was skipped entirely (it required a fresh
    // mediaFile), so the copy silently submitted with no header at all even
    // though the form still visibly showed the original image — carry the
    // existing URL forward as header_content instead of dropping it.
    const existingMediaUrl =
      formData.mediaType === 'image' ? formData.headerImage
        : formData.mediaType === 'video' ? formData.headerVideo
          : formData.headerDocument;
    if (existingMediaUrl) {
      switch (formData.mediaType) {
        case 'image': headerType = "image"; break;
        case 'video': headerType = "video"; break;
        case 'document': headerType = "document"; break;
        default: headerType = "image";
      }
      headerContent = existingMediaUrl;
    }
  }

  // Base payload for all channels
  const payload: any = {
    channel: formData.channel === 'whatsapp' ? "WhatsApp"
      : formData.channel === 'sms' ? "SMS"
      : formData.channel === 'rcs' ? "RCS"
      : "Email",
    category: formData.category,
    template_name: formData.templateName,
    language: formData.language,
  };

  // WhatsApp specific
  if (formData.channel === 'whatsapp') {
    if (formData.whatsappBusinessAccount) {
      payload.waba_id = formData.whatsappBusinessAccount;
    }

    payload.content = formData.messageBody;
    if (formData.footer) {
      payload.footer = formData.footer;
    }

    if (headerType === 'text' && headerContent) {
      payload.header_type = headerType;
      payload.header_content = headerContent;
    } else if (headerType !== 'none' && headerFile) {
      // Real file upload — attached to the FormData below as "header_file"
      // instead of sending just the filename as header_content.
      payload.header_type = headerType;
    } else if (headerType !== 'none' && headerContent) {
      // Media header, no fresh file picked — reuse the existing media URL.
      payload.header_type = headerType;
      payload.header_content = headerContent;
    }

    const variableIndices = extractVariableIndices(formData.messageBody || "");
    if (variableIndices.length > 0) {
      payload.variables = variableIndices.map((index) => {
        const found = (formData.messageVariables || []).find((v) => v.index === index);
        return {
          index,
          name: found?.name?.trim() || `variable_${index}`,
          example: found?.example?.trim() || "",
        };
      });
    }

    if (formData.buttonType === 'quick_reply') {
      const filled = formData.quickReplyButtons.filter((btn) => btn.label.trim());
      if (filled.length > 0) {
        payload.buttons = filled.map((btn) => ({ type: 'QUICK_REPLY', text: btn.label.trim() }));
      }
    } else if (formData.buttonType === 'call_to_action') {
      // Per-type "is this button actually filled in" check — VOICE_CALL only
      // needs its label; URL/PHONE_NUMBER/COPY_CODE need their destination
      // (url/phone_number/example) filled in too. Confirmed shapes
      // (2026-08-18): COPY_CODE DOES send `text` (defaults to "Copy offer
      // code", not omitted), and `example` is a plain string on both
      // COPY_CODE and dynamic URL — not wrapped in an array as I'd guessed.
      const filled = (formData.ctaButtons || []).filter((btn) => {
        if (btn.type === 'URL') return btn.text.trim() && btn.url?.trim();
        if (btn.type === 'PHONE_NUMBER') return btn.text.trim() && btn.phoneNumber?.trim();
        if (btn.type === 'VOICE_CALL') return btn.text.trim();
        if (btn.type === 'COPY_CODE') return btn.example?.trim();
        return btn.text.trim();
      });
      if (filled.length > 0) {
        payload.buttons = filled.map((btn) => {
          if (btn.type === 'URL') {
            return {
              type: 'URL',
              text: btn.text.trim(),
              url: btn.url?.trim(),
              // Dynamic URL needs a full example URL ({{1}} resolved to a
              // sample value) for template review.
              ...(btn.urlType === 'dynamic' && btn.example?.trim() ? { example: btn.example.trim() } : {}),
            };
          }
          if (btn.type === 'PHONE_NUMBER') {
            return { type: 'PHONE_NUMBER', text: btn.text.trim(), phone_number: btn.phoneNumber?.trim() };
          }
          if (btn.type === 'VOICE_CALL') {
            return { type: 'VOICE_CALL', text: btn.text.trim() };
          }
          return { type: 'COPY_CODE', text: btn.text.trim() || 'Copy offer code', example: btn.example!.trim() };
        });
      }
    }
  }

  // SMS specific
  else if (formData.channel === 'sms') {
    payload.content = formData.messageBody;
    if (formData.footer) {
      payload.footer = formData.footer;
    }

    // Confirmed real payload shape (2026-08-14):
    // { channel: "SMS", ..., variables: [{ index, name, example }] }, same
    // as WhatsApp's branch above — EXCEPT built from formData.messageVariables
    // directly rather than extractVariableIndices(formData.messageBody).
    // SMS's "Add variable" does a literal, permanent substitution into the
    // body once an example is typed (confirmed choice), so by submit time
    // the {{n}} token is often already gone from the text — scanning the
    // body would silently drop every variable that actually got filled in,
    // which was exactly the bug: "I add variables but they're not in the
    // payload". formData.messageVariables is the persisted list (survives
    // substitution — see SmsMessageVariablesSection in sms-form.tsx), so it
    // stays the source of truth here.
    const smsVariables = (formData.messageVariables || []).slice().sort((a, b) => a.index - b.index);
    if (smsVariables.length > 0) {
      payload.variables = smsVariables.map((v) => ({
        index: v.index,
        name: v.name?.trim() || `variable_${v.index}`,
        example: v.example?.trim() || "",
      }));
    }
  }

  // Email specific
  else if (formData.channel === 'email') {
    // Both tabs keep emailHtmlContent current (Plain Text writes its markup
    // there too), so it's the one source — emailPlainText used to override
    // it even after the user moved on to the Email Editor.
    const html = formData.emailHtmlContent || formData.emailPlainText || '';
    if (html) {
      payload.html_content = html;
      // The Email Editor's design JSON (reopens as editable blocks — see
      // email-design.ts), or the text itself for a Plain Text email.
      payload.content = formData.emailDesign ? JSON.stringify(formData.emailDesign) : htmlToPlainText(html);
    }
    if (formData.emailSubject) {
      payload.subject = formData.emailSubject;
    }
    if (formData.emailPreviewText) {
      payload.preview_text = formData.emailPreviewText;
    }
    if (formData.emailSenderEmail) {
      payload.sender_email = formData.emailSenderEmail;
    }
    if (formData.emailSenderName) {
      payload.sender_name = formData.emailSenderName;
    }
    if (formData.emailVariables?.length) {
      payload.default_variables = formData.emailVariables;
    }
  }

  // RCS specific — same /templates/ endpoint as every other channel,
  // just with its own component shape instead of header/content/buttons,
  // and its own category/language conventions (uppercase category, short
  // language code) instead of WhatsApp/SMS/Email's title-case versions.
  else if (formData.channel === 'rcs') {
    payload.category = formData.category.toUpperCase();
    payload.language = RCS_LANGUAGE_CODE[formData.language] || 'en';
    if (formData.rcsAgentId) {
      payload.rcs_agent_id = formData.rcsAgentId;
    }
    payload.components = mapRcsComponentsToApi(rcsComponentsOverride ?? formData.rcsComponents ?? []);
  }

  const fd = new FormData();
  fd.append("data", JSON.stringify(payload));
  if (headerFile) {
    fd.append("header_file", headerFile);
  }
  return fd;
};

  // PUT /templates/{id} body for an existing SMS template — confirmed shape
  // (2026-08-14): { content: { message, personalize, variable_column_map,
  // variables } }, a plain JSON object (not the FormData wrapper
  // prepareTemplatePayload uses for create, and not the full template
  // payload — just its content). `variables` here is a flat name→value map
  // (unlike create's [{index,name,example}] array) — built from whichever
  // rows in formData.messageVariables actually have an example value.
  // variable_column_map stays empty — there's no CSV-mapping concept in the
  // template creator, only in campaigns.
  const prepareSmsUpdatePayload = () => {
    const variables: Record<string, string> = {};
    (formData.messageVariables || []).forEach((v) => {
      const example = v.example?.trim();
      if (example) {
        variables[v.name?.trim() || `variable_${v.index}`] = example;
      }
    });
    // Confirmed by a real 422 (2026-08-14): "content" must be a plain
    // string, not a nested object — the earlier sample's { content: { ... } }
    // wrapper was wrong. personalize/variable_column_map/variables are
    // siblings of content, all at the top level.
    return {
      content: formData.messageBody,
      personalize: false,
      variable_column_map: {},
      variables,
    };
  };

  const getPreviewProps = () => {
    const vars: Record<string, string> = {};
    (formData.messageVariables || []).forEach((v) => {
      vars[`variable_${v.index}`] = v.example || "";
    });
    // WhatsappMockUp reads buttons from `template.buttons` (matching the real
    // backend template shape used elsewhere), not a top-level `buttons` prop
    // — this used to pass the latter, so the preview never actually rendered
    // any button at all.
    let previewButtons: { text: string; url?: string }[] | undefined;
    if (formData.buttonType === 'quick_reply') {
      previewButtons = formData.quickReplyButtons;
    } else if (formData.buttonType === 'call_to_action') {
      previewButtons = (formData.ctaButtons || []).map((btn) => ({
        // COPY_CODE has no user-editable label (WhatsApp shows a fixed
        // "Copy offer code" button) — fall back to that so the preview
        // doesn't show a blank button.
        text: btn.type === 'COPY_CODE' ? (btn.text || 'Copy offer code') : btn.text,
        url: btn.type === 'URL' ? btn.url : undefined,
      }));
    }
    // WhatsappMockUp reads the header the same way it reads buttons — off
    // `template.header_type`/`template.header_content` — so a freshly-picked
    // local file (via its blob URL) and an already-hydrated existing
    // template's own header (headerImage/headerVideo/headerDocument, set by
    // hydrateHeaderFromTemplate when viewing/copying an approved template)
    // both flow through the exact same path instead of needing two.
    let headerType: string | undefined;
    let headerContent: string | undefined;
    if (formData.headerType === 'text') {
      headerType = 'text';
      headerContent = formData.headerText;
    } else if (formData.headerType === 'media') {
      headerType = formData.mediaType;
      const freshUrl = formData.mediaFile ? URL.createObjectURL(formData.mediaFile) : '';
      const existingUrl =
        formData.mediaType === 'image' ? formData.headerImage
          : formData.mediaType === 'video' ? formData.headerVideo
            : formData.headerDocument;
      headerContent = freshUrl || existingUrl || undefined;
    }
    const previewProps: any = {
      message: formData.messageBody,
      footer: formData.footer,
      template: { buttons: previewButtons, header_type: headerType, header_content: headerContent },
      vars,
    };
    return previewProps;
  };

  if (!isOpen) return null;

  const fieldDisabled = isReadOnly;

  // Live Preview only — the actual Message Body / submitted content always
  // keeps its real {{n}} placeholders (see sms-form.tsx). This just resolves
  // them to their example values for display, same idea as WhatsApp's own
  // preview (getPreviewProps below passes `vars` for the same reason).
  // Unmapped/blank ones fall back to bare "{{}}" rather than showing the
  // raw {{n}}, matching how the campaign composer's preview already does it.
  const resolveSmsPreviewMessage = (): string => {
    const body: string = formData.messageBody || "";
    const vars = formData.messageVariables || [];
    return body.replace(/{{(\d+)}}/g, (_full, idx) => {
      const found = vars.find((v) => String(v.index) === idx);
      const example = found?.example?.trim();
      return example || "{{}}";
    });
  };

  const getSmsPreviewProps = () => ({
    message: resolveSmsPreviewMessage(),
    shortenedUrl: (formData as any).shortenUrl ? (formData as any).shortenedUrl : undefined,
  });

const handleCreateTemplate = async () => {
  if (!formData.templateName.trim()) { toast.error("Template name is required"); return; }
  if (!formData.category) { toast.error("Category is required"); return; }
  if (!formData.language) { toast.error("Language is required"); return; }
  if (formData.channel === 'whatsapp' && !formData.whatsappBusinessAccount) {
    toast.error("WhatsApp Business Account is required"); return;
  }
  
  // Email validation - FIXED
  if (formData.channel === 'email') {
    if (!formData.emailSubject?.trim()) { 
      toast.error("Email subject is required"); 
      return; 
    }

    // Check for either html_content OR plain_text
    if (!formData.emailHtmlContent?.trim() && !formData.emailPlainText?.trim()) {
      toast.error("Email content is required (HTML or plain text)"); 
      return; 
    }
  } 
  // WhatsApp validation - ensure message body has proper format
  else if (formData.channel === 'whatsapp') {
    if (!formData.messageBody.trim()) {
      toast.error("Message body is required");
      return;
    }
    // WhatsApp requires at least one character of text
    if (formData.messageBody.trim().length === 0) {
      toast.error("Message body cannot be empty");
      return;
    }
  }
  // RCS validation is handled by RcsForm/rcsComponents, not messageBody
  else if (formData.channel === 'rcs') {
    // no-op — falls through to the RCS submit branch below
  }
  // SMS validation
  else if (!formData.messageBody.trim()) {
    toast.error("Message body is required");
    return;
  }

  setIsSubmitting(true);
  try {
    // Editing an existing SMS template (not a copy, which should create a
    // new one) — PUT its content instead of POSTing a duplicate. Other
    // channels don't have a confirmed update contract yet, so they keep the
    // existing create-only behavior.
    if (selectedTemplate && !isCopyMode && formData.channel === 'sms') {
      const [response, status] = await updateSmsTemplate(prepareSmsUpdatePayload(), undefined, selectedTemplate.id);
      if (status >= 200 && status < 300) {
        toast.success(response?.message || "Template updated successfully!");
        setFormData(defaultFormData);
        onClose?.();
        onSendRequest?.(formData);
      } else {
        toast.error(response?.detail || response?.message || "Failed to update template");
      }
      return;
    }

    let rcsComponentsToSubmit = formData.rcsComponents;
    if (formData.channel === 'rcs') {
      const hasMediaFiles = (formData.rcsComponents || []).some(
        (comp) => comp.mediaFile || comp.cards?.some((card) => card.mediaFile)
      );
      if (hasMediaFiles) {
        rcsComponentsToSubmit = await uploadAllRcsMedia();
      }
    }

    const payload = prepareTemplatePayload(rcsComponentsToSubmit);

    const [response] = await createTemplate(payload);
    if (response?.status) {
      toast.success(response?.message || "Template created successfully!");
      setFormData(defaultFormData);
      onClose?.();
      onSendRequest?.(formData);
    } else if (response?.detail) {
      toast.error(response.type || response.detail || "Failed to create template");
    }
  } catch (error) {
    console.error("Error creating template:", error);
    toast.error("An error occurred while creating the template");
  } finally {
    setIsSubmitting(false);
  }
};

  const handleSaveDraft = async () => {
    if (!formData.templateName.trim()) { toast.error("Template name is required to save as draft"); return; }
    setIsSubmitting(true);
    try {
      // Same update-vs-create branch as handleCreateTemplate — there's no
      // separate "draft" PUT contract, so this hits the same update endpoint.
      if (selectedTemplate && !isCopyMode && formData.channel === 'sms') {
        const [response, status] = await updateSmsTemplate(prepareSmsUpdatePayload(), undefined, selectedTemplate.id);
        if (status >= 200 && status < 300) {
          toast.success(response?.message || "Template updated!");
          onSaveDraft?.(formData);
        } else {
          toast.error(response?.detail || response?.message || "Failed to update template");
        }
        return;
      }

      let rcsComponentsToSubmit = formData.rcsComponents;
      if (formData.channel === 'rcs') {
        const hasMediaFiles = (formData.rcsComponents || []).some(
          (comp) => comp.mediaFile || comp.cards?.some((card) => card.mediaFile)
        );
        if (hasMediaFiles) {
          rcsComponentsToSubmit = await uploadAllRcsMedia();
        }
      }

      const payload = prepareTemplatePayload(rcsComponentsToSubmit);
      const [response, error] = await createTemplate(payload);
      if (response?.status) {
        toast.success(response?.message || "Template saved as draft!");
        onSaveDraft?.(formData);
      } else if (error) {
        toast.error(error.message || "Failed to save draft");
      }
    } catch (error) {
      toast.error("An error occurred while saving the draft");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  // ── CHANGE 1: email handler for Emailform onChange ──────────────────────────
  const handleEmailChange = (updates: Partial<EmailFormData>) => {
    handleChange({
      ...(updates.subject      !== undefined && { emailSubject:      updates.subject }),
      ...(updates.previewText  !== undefined && { emailPreviewText:  updates.previewText }),
      ...(updates.senderEmail  !== undefined && { emailSenderEmail:  updates.senderEmail }),
      ...(updates.senderName   !== undefined && { emailSenderName:   updates.senderName }),
      ...(updates.plainText    !== undefined && { emailPlainText:    updates.plainText }),
      ...(updates.htmlContent  !== undefined && { emailHtmlContent:  updates.htmlContent }),
      ...(updates.variables    !== undefined && { emailVariables:    updates.variables }),
      ...(updates.design       !== undefined && { emailDesign:       updates.design }),
    });
  };

  const renderChannelForm = () => {
    switch (formData.channel) {
      case 'whatsapp':
        return <WhatsAppForm wabaAccounts={wabaAccounts} formData={formData} onChange={handleChange} />;
      case 'sms':
        return <SmsForm formData={formData as any} onChange={handleChange} />;
      // ── CHANGE 2: wire up Emailform ────────────────────────────────────────
      case 'email':
        return (
          <Emailform
            formData={{
              subject:     formData.emailSubject     ?? '',
              previewText: formData.emailPreviewText ?? '',
              senderEmail: formData.emailSenderEmail ?? '',
              senderName:  formData.emailSenderName  ?? '',
              plainText:   formData.emailPlainText   ?? '',
              htmlContent: formData.emailHtmlContent ?? '',
              variables:   formData.emailVariables   ?? [],
              design:      formData.emailDesign      ?? null,
            }}
            onChange={handleEmailChange}
            readOnly={fieldDisabled}
          />
        );
      case 'rcs':
        return <RcsForm formData={formData} onChange={handleChange} readOnly={fieldDisabled} />;
      default:
        return null;
    }
  };

  // ── CHANGE 3: include email + rcs in showPreview ────────────────────────────
  const showPreview =
    formData.channel === 'whatsapp' ||
    formData.channel === 'sms' ||
    formData.channel === 'email' ||
    formData.channel === 'rcs';

  // ── CHANGE 4: add email branch in renderPreview ─────────────────────────────
  const renderPreview = () => {
    if (formData.channel === 'whatsapp') {
      return (
        <div className="hidden md:flex flex-col border-l border-gray-100 dark:border-gray-800 flex-shrink-0" style={{ width: 300 }}>
          <div className="bg-green-50 border-b border-green-100 px-5 py-3">
            <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">Live Preview</h3>
          </div>
          <div className="flex-1 flex items-start justify-center p-6 bg-gray-50 dark:bg-gray-800 overflow-y-auto">
            <WhatsappMockUp {...getPreviewProps()} />
          </div>
        </div>
      );
    } else if (formData.channel === 'sms') {
      return (
        <div className="hidden md:flex flex-col border-l border-gray-100 dark:border-gray-800 flex-shrink-0" style={{ width: 300 }}>
          <div className="bg-green-50 border-b border-green-100 px-5 py-3">
            <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">Live Preview</h3>
          </div>
          <div className="flex-1 flex items-start justify-center p-6 bg-gray-50 dark:bg-gray-800 overflow-y-auto">
            <PhoneMockup {...getSmsPreviewProps()} />
          </div>
        </div>
      );
    } else if (formData.channel === 'email') {
      return (
        <div className="hidden md:flex flex-col border-l border-gray-100 dark:border-gray-800 flex-shrink-0" style={{ width: 300 }}>
          <div className="bg-green-50 border-b border-green-100 px-5 py-3">
            <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">Live Preview</h3>
          </div>
          <div className="flex-1 flex items-start justify-center p-6 bg-gray-50 dark:bg-gray-800 overflow-y-auto">
            <EmailMockUp
              subject={fillEmailVariables(formData.emailSubject ?? '', formData.emailVariables)}
              previewText={fillEmailVariables(formData.emailPreviewText ?? '', formData.emailVariables)}
              senderName={formData.emailSenderName ?? ''}
              senderEmail={formData.emailSenderEmail ?? ''}
              htmlContent={fillEmailVariables(formData.emailHtmlContent ?? '', formData.emailVariables)}
              plainText={fillEmailVariables(formData.emailPlainText ?? '', formData.emailVariables)}
            />
          </div>
        </div>
      );
    } else if (formData.channel === 'rcs') {
      return (
        <div className="hidden md:flex flex-col border-l border-gray-100 dark:border-gray-800 flex-shrink-0" style={{ width: 300 }}>
          <div className="bg-blue-50 border-b border-blue-100 px-5 py-3">
            <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">Live Preview</h3>
          </div>
          <div className="flex-1 flex items-start justify-center p-6 bg-gray-50 dark:bg-gray-800 overflow-y-auto">
            <RcsPreview components={formData.rcsComponents || []} />
          </div>
        </div>
      );
    }
    return null;
  };

  const content = (
      <div
        className={`relative bg-white dark:bg-gray-900 flex flex-col overflow-hidden ${asDrawer ? 'h-full' : 'mt-4 rounded-2xl shadow-2xl w-full h-screen'}`}
      >
        {isSubmitting && formData.channel === 'rcs' && (
          <div className="absolute inset-0 z-50 bg-white/80 dark:bg-gray-900/80 backdrop-blur-sm flex flex-col items-center justify-center">
            <div className="w-56 h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
              <div
                className="h-full bg-indigo-600 rounded-full"
                style={{ width: '40%', animation: 'progressScroll 1.2s ease-in-out infinite' }}
              />
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-300 mt-3">Creating template...</p>
            <style>{`
              @keyframes progressScroll {
                0% { transform: translateX(-100%); }
                100% { transform: translateX(250%); }
              }
            `}</style>
          </div>
        )}
        {/* Header */}
        <div className="flex items-start justify-between px-6 py-4 border-b border-gray-100 dark:border-gray-800 flex-shrink-0">
          <div>
            <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100 flex items-center">
              {selectedTemplate
                ? isCopyMode
                  ? `${selectedTemplate.template_name}_Copy`
                  : selectedTemplate.template_name
                : 'New template'}
              {!isCopyMode && titleBadge}
            </h2>
            <p className="text-xs text-[#7B7B7B] font-semibold dark:text-gray-500 mt-0.5">
              {isReadOnly
                ? 'This template is read-only. Click "Make Copy" to create an editable copy.'
                : selectedTemplate
                  ? 'Edit and resubmit this template.'
                  : 'Request a new template'}
            </p>
          </div>
          <button type="button" onClick={onClose} className="text-[#7B7B7B] font-semibold dark:text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 transition-colors p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700">
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="flex flex-1 overflow-hidden min-h-0">
          <div className={`flex flex-col overflow-hidden ${showPreview ? 'flex-1' : 'w-full'}`}>
            <div className={`flex-1 overflow-y-auto px-6 py-5 space-y-5 ${isReadOnly ? 'pointer-events-none opacity-75' : ''}`}>
              {/* Channel + Template Name */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <SectionLabel label="Channel" />
                  <ChannelSelect
                    value={formData.channel}
                    onChange={handleChannelChange}
                    options={channelOptions}
                  />
                </div>
                <div>
                  <SectionLabel label="Template Name" />
                  <Input
                    value={formData.templateName}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      handleChange({ templateName: e.target.value })
                    }
                    placeholder="Add a template name"
                    readOnly={fieldDisabled}
                  />
                </div>
              </div>

              {/* Channel-specific form */}
              {renderChannelForm()}
            </div>

            {/* Footer actions */}
            <div className="flex items-center justify-between px-6 py-4 border-t border-gray-100 dark:border-gray-800 bg-white dark:bg-gray-900 flex-shrink-0">
              {isReadOnly ? (
                <>
                  <span />
                  <div className="flex items-center gap-2">
                    <button type="button" onClick={onClose} className="text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100 px-4 py-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
                      Discard
                    </button>
                    <button
                      type="button"
                      onClick={handleMakeCopy}
                      className="text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg px-5 py-2 transition-colors shadow-sm"
                    >
                      Make Copy
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <button
                    type="button"
                    onClick={handleSaveDraft}
                    disabled={isSubmitting}
                    className="text-sm font-medium text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 rounded-lg px-4 py-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? 'Saving...' : 'Save as Draft'}
                  </button>
                  <div className="flex items-center gap-2">
                    <button type="button" onClick={onClose} className="text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100 px-4 py-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
                      Discard
                    </button>
                    <button
                      type="button"
                      onClick={handleCreateTemplate}
                      disabled={isSubmitting}
                      className="text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg px-5 py-2 transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isSubmitting ? 'Submitting...' : 'Submit'}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Live Preview panel */}
          {renderPreview()}
        </div>
      </div>
  );

  if (asDrawer) return content;

  return (
    <div className="fixed inset-0 z-50 py-4 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
      {content}
    </div>
  );
};